import type { BillSplit } from "@/features/user/types"

type SplitResult = { ok: true; split: BillSplit | null } | { ok: false; errors: string[] }

/** Opens the table's agreement to divide the bill into `payerCount` shares. */
export async function openBillSplit(billId: number, payerCount: number): Promise<SplitResult> {
  return request(`/bills/${billId}/split`, "POST", { payer_count: payerCount })
}

/**
 * Undoes it. The server decides who is allowed: any diner until the first
 * share is paid, staff only afterwards.
 */
export async function cancelBillSplit(billId: number): Promise<SplitResult> {
  return request(`/bills/${billId}/split`, "DELETE")
}

async function request(url: string, method: string, body?: Record<string, unknown>): Promise<SplitResult> {
  const response = await fetch(url, {
    method,
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
      "X-Requested-With": "XMLHttpRequest",
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })

  const payload = await parseJson(response)

  if (!response.ok) {
    return { ok: false, errors: errorsFrom(payload, response.statusText) }
  }

  return { ok: true, split: (payload.split as BillSplit | null) ?? null }
}

function csrfToken() {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
}

async function parseJson(response: Response) {
  try {
    return (await response.json()) as Record<string, unknown>
  } catch {
    return {}
  }
}

function errorsFrom(payload: Record<string, unknown>, fallback: string) {
  if (Array.isArray(payload.errors)) return payload.errors.map(String)
  if (typeof payload.error === "string") return [payload.error]

  return [fallback]
}
